// ============================================================================
// Fiscal adapters — per-country invoicing rules behind ONE interface.
// ----------------------------------------------------------------------------
// The app was built Spain-first: IVA/IRPF/IBAE tax math (lib/taxes.js) and the
// Verifactu / TicketBAI / Facturae artifacts are all AEAT-shaped. To serve more
// countries we route every country-specific decision in the INVOICING flow
// through a fiscal adapter, chosen by the account owner's `profiles.country_code`
// (see backend/country_localization.sql).
//
// The materials ERP is country-neutral and does NOT go through here — only
// invoices / estimates / tickets do.
//
// Adapter contract (see ./es.js for the reference implementation):
//   countryCode                         'ES' | 'PK'
//   meta       { name, currency, currencySymbol, locale,
//                taxIdLabel, clientTaxIdLabel }
//   taxFeatures{ recargoEquiv, ibae, irpf }   // which tax controls the editor shows
//   computeTotals(lines, opts) -> {           // normalized totals for UI + template
//                byRate, subtotal, taxTotal, taxLabel,
//                extraTotals:[{key,label,amount}],
//                deductions:[{key,label,amount}], grandTotal }
//   requiredClientFields()      -> string[]   // e.g. ['tax_id'] | ['tax_id','hs_code']
//   validateTaxId(id)           -> boolean
//   einvoice   { enabled, featureKey, label,
//                submit({invoice,lines,profile,client}) -> Promise<{fiscalNumber,qr,status,raw}> }
//   einvoicePra (optional) — a SECOND authority with the same contract. Pakistan
//                taxes goods federally (FBR = einvoice) and services provincially
//                (PRA, Punjab = einvoicePra); a PK account offers both.
//
// Phase 0 only defines the adapters + this registry. The invoicing pages are
// wired to them in Phase 1; until then the Spanish path is unchanged.
// ============================================================================
import { supabase } from '../supabase';
import { getOwnerId } from '../tenant';
import { esAdapter } from './es';
import { pkAdapter } from './pk';
import { adapterForTaxSystem, normaliseTaxSystem } from '../taxSystem';

export const DEFAULT_COUNTRY = 'ES';

// Registry. Add a country by dropping in ./xx.js and registering it here.
const ADAPTERS = {
  ES: esAdapter,
  PK: pkAdapter,
};

// Countries the UI may offer (admin country selector, onboarding). Mirrors the
// registry keys; label is shown in pickers.
export const SUPPORTED_COUNTRIES = [
  { code: 'ES', name: 'España',   currency: 'EUR', taxAuthority: 'AEAT (Verifactu)' },
  { code: 'PK', name: 'Pakistan', currency: 'PKR', taxAuthority: 'FBR (Digital Invoicing) · PRA (Punjab e-Invoicing)' },
];

// Resolve an adapter from a profile object or a bare country code. Always
// returns a valid adapter (falls back to the default country) so callers never
// have to null-check.
// `taxSystem` (Spain only): 'iva' | 'igic' | 'ipsi' — read from the profile
// object when one is passed, else from the owner cache. IVA returns the plain
// Spain adapter, so every account that never touched the setting is unchanged.
export function getAdapter(profileOrCode, taxSystem = null) {
  const isProfile = !!profileOrCode && typeof profileOrCode === 'object';
  const code = typeof profileOrCode === 'string'
    ? profileOrCode
    : (profileOrCode?.country_code || DEFAULT_COUNTRY);
  const base = ADAPTERS[String(code || DEFAULT_COUNTRY).toUpperCase()] || ADAPTERS[DEFAULT_COUNTRY];
  if (base.countryCode !== 'ES') return base;
  const sys = taxSystem
    || (isProfile && profileOrCode.tax_system)
    || (_cache && _cache.taxSystem)
    || getCachedTaxSystem();
  return adapterForTaxSystem(base, sys);
}

// --- Owner country-code cache (mirrors the tenant.js pattern) --------------
// The active fiscal country is a property of the account OWNER, so team members
// inherit it. Cached per owner so the invoicing pages don't refetch on mount.
let _cache = null; // { ownerId, countryCode, taxSystem }

// Last country resolved on this browser, persisted so country-driven chrome
// (e.g. the Layout language switcher) can render right on first paint instead
// of flashing the ES default while the profile fetch is in flight.
const LAST_COUNTRY_KEY = 'zbilling_last_country';
const LAST_TAX_KEY = 'zbilling_last_tax_system';

export function getCachedCountryCode() {
  try {
    const code = localStorage.getItem(LAST_COUNTRY_KEY);
    return code && ADAPTERS[code] ? code : null;
  } catch { return null; }
}

/** Last Spanish tax system resolved on this browser ('iva' when unknown). */
export function getCachedTaxSystem() {
  try { return normaliseTaxSystem(localStorage.getItem(LAST_TAX_KEY)); } catch { return 'iva'; }
}

export function clearCountryCache() {
  _cache = null;
}

// Fetch the current account owner's country_code (defaults to 'ES') and, for
// Spain, the tax system. Fails safe to the default so a missing column /
// policy never breaks invoicing: a database without profiles.tax_system
// (backend/tax_system_and_contacts.sql) is asked for the country alone.
export async function fetchCountryCode() {
  const ownerId = await getOwnerId();
  if (!ownerId) return DEFAULT_COUNTRY;
  if (_cache && _cache.ownerId === ownerId) return _cache.countryCode;
  let { data, error } = await supabase
    .from('profiles')
    .select('country_code, tax_system')
    .eq('id', ownerId)
    .single();
  if (error && (String(error.code) === '42703' || /tax_system|schema cache/i.test(error.message || ''))) {
    ({ data, error } = await supabase.from('profiles').select('country_code').eq('id', ownerId).single());
  }
  if (error) {
    console.warn('fetchCountryCode failed:', error.message);
    return DEFAULT_COUNTRY;
  }
  const countryCode = data?.country_code || DEFAULT_COUNTRY;
  const taxSystem = normaliseTaxSystem(data?.tax_system);
  _cache = { ownerId, countryCode, taxSystem };
  try {
    localStorage.setItem(LAST_COUNTRY_KEY, countryCode);
    localStorage.setItem(LAST_TAX_KEY, taxSystem);
  } catch { /* ignore */ }
  return countryCode;
}

/** The owner's tax system ('iva' | 'igic' | 'ipsi'), resolving the profile if needed. */
export async function fetchTaxSystem() {
  await fetchCountryCode();
  return (_cache && _cache.taxSystem) || getCachedTaxSystem();
}
